import axios, { AxiosError } from 'axios';
import type { ApiResult } from './types';
import { env } from '@/lib/env';
import { useAuthStore } from '@/store/auth-store';

export class BizError extends Error {
  constructor(
    public code: number,
    message: string,
  ) {
    super(message);
    this.name = 'BizError';
  }
}

type UnauthorizedHandler = (redirectPath: string) => void;

let unauthorizedHandler: UnauthorizedHandler | null = null;

// Guard: 并发多个 401 不应触发多次 clear + navigate;再次 401 时如果 token 已被清,说明其他请求已处理过。
let unauthorizedInFlight = false;

export function buildApiBaseUrl(apiBaseUrl: string, useDevProxy = import.meta.env.DEV): string {
  if (useDevProxy) {
    return '/api';
  }
  return `${apiBaseUrl.replace(/\/+$/, '')}/v1`;
}

/**
 * Register the 401 handler. The router is created in `main.tsx` AFTER this module
 * loads, so we cannot import the router here. This module-level indirection is a
 * project-specific escape hatch, not a common pattern.
 *
 * Idempotent: re-registering (e.g. on Vite HMR) just swaps the handler.
 * If you switch to a different routing lib, update `main.tsx` only.
 */
export function setUnauthorizedHandler(handler: UnauthorizedHandler): void {
  unauthorizedHandler = handler;
}

export const apiClient = axios.create({
  baseURL: buildApiBaseUrl(env.VITE_API_BASE_URL),
  timeout: 20_000,
  headers: { 'Content-Type': 'application/json' },
});

apiClient.interceptors.request.use((config) => {
  const token = useAuthStore.getState().token;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => {
    const body = response.data as ApiResult<unknown>;
    if (body && typeof body === 'object' && 'code' in body) {
      if (body.code !== 0) {
        return Promise.reject(
          new BizError(body.code, body.msg || body.message || 'Unknown business error'),
        );
      }
      return { ...response, data: body.data };
    }
    return response;
  },
  (error: AxiosError<ApiResult<unknown>>) => {
    if (error.response?.status === 401) {
      // 并发 401 只触发一次 clear + navigate,避免 history 堆栈堆多条 /login 或浏览器闪烁。
      if (!unauthorizedInFlight) {
        unauthorizedInFlight = true;
        // 登录页上发出的请求(如 getUserInfo)若返回 401,不清 token ——
        // 此时 token 可能刚由 login 写入,清掉会导致跳转 /ads/accounts 后被打回 /login。
        if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
          useAuthStore.getState().clear();
          const path = window.location.pathname + window.location.search;
          if (unauthorizedHandler) {
            unauthorizedHandler(path);
          } else {
            window.location.replace(`/login?redirectPath=${encodeURIComponent(path)}`);
          }
        }
        // 下一轮 event loop 解锁,允许登录页重新挂载后再次触发(若用户 token 再失效)。
        setTimeout(() => {
          unauthorizedInFlight = false;
        }, 0);
      }
    }
    const bizMessage = error.response?.data?.msg || error.response?.data?.message;
    if (bizMessage) {
      return Promise.reject(new BizError(error.response?.data?.code ?? -1, bizMessage));
    }
    return Promise.reject(error);
  },
);
